import React from "react";

import type { ChecklistItem, ChecklistStatus } from "../../features/process/progress/processMappingProgress";

function statusClasses(status: ChecklistStatus): {
  line: string;
  icon: string;
  textClass: string;
  iconBg: string;
  iconBorder: string;
} {
  switch (status) {
    case "complete":
      return {
        line: "rgba(var(--tenant-primary-rgb, 92, 158, 251), 0.55)",
        icon: "fa-solid fa-check text-white",
        textClass: "text-light",
        iconBg: "var(--tenant-gradient, linear-gradient(120deg, var(--tenant-primary, #5c9efb), var(--tenant-secondary, #017d7c)))",
        iconBorder: "1px solid rgba(var(--tenant-primary-rgb, 92, 158, 251), 0.55)",
      };
    case "current":
      return {
        line: "rgba(var(--tenant-primary-rgb, 92, 158, 251), 0.35)",
        icon: "fa-solid fa-spinner fa-spin",
        textClass: "text-light",
        iconBg: "rgba(var(--tenant-primary-rgb, 92, 158, 251), 0.14)",
        iconBorder: "2px solid rgba(var(--tenant-primary-rgb, 92, 158, 251), 0.9)",
      };
    default:
      return {
        line: "rgba(148, 163, 184, 0.2)",
        icon: "fa-regular fa-circle",
        textClass: "new-text-secondary",
        iconBg: "transparent",
        iconBorder: "2px solid rgba(148,163,184,0.45)",
      };
  }
}

export default function MappingProgressChecklist(props: {
  items: ChecklistItem[];
  progressPercent?: number;
}) {
  const { items, progressPercent } = props;

  return (
    <nav aria-label="Progress">
      <div
        className="p-2 rounded-3"
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          backdropFilter: "none",
          boxShadow: "none",
        }}
      >
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h4 className="text-gradient fs-6 mb-0 text-nowrap">Checklist</h4>
          {typeof progressPercent === "number" && (
            <div className="small" style={{ color: "rgba(229,231,235,0.8)" }}>
              {progressPercent}%
            </div>
          )}
        </div>

        {typeof progressPercent === "number" && (
          <div className="progress mb-3" style={{ height: 7, backgroundColor: "rgba(255,255,255,0.08)" }}>
            <div
              className="progress-bar"
              role="progressbar"
              style={{
                width: `${Math.max(0, Math.min(100, progressPercent))}%`,
                background: "var(--tenant-gradient, linear-gradient(90deg, var(--tenant-primary, #5c9efb), var(--tenant-secondary, #017d7c)))",
              }}
              aria-valuenow={progressPercent}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}

        <ol className="list-unstyled mb-0">
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            const cls = statusClasses(item.status);

            return (
              <li key={item.id} className={`position-relative ${!isLast ? "pb-3" : ""}`}>
                {!isLast && (
                  <div
                    aria-hidden="true"
                    className="position-absolute"
                    style={{
                      left: 14,
                      top: 28,
                      bottom: -8,
                      width: 2,
                      background: cls.line,
                    }}
                  />
                )}

                <div className="d-flex align-items-start">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle position-relative"
                    style={{
                      width: 28,
                      height: 28,
                      background: cls.iconBg,
                      border: cls.iconBorder,
                    }}
                  >
                    <i
                      className={cls.icon}
                      aria-hidden="true"
                      style={{ color: item.status === "upcoming" ? "rgba(148,163,184,0.9)" : "#e5e7eb" }}
                    />
                  </div>

                  <div className="ms-2 min-w-0">
                    <div className={`d-flex align-items-center ${cls.textClass}`} style={{ fontSize: 18, lineHeight: 1.2 }}>
                      <span className="fw-medium">{item.label}</span>
                      {item.aiActive && (
                        <span
                          className="ms-2 badge rounded-pill"
                          style={{
                            color: "#34d399",
                            border: "1px solid rgba(52,211,153,0.42)",
                            backgroundColor: "rgba(6,95,70,0.24)",
                            fontWeight: 600,
                          }}
                        >
                          <span
                            className="me-1 d-inline-block rounded-circle animate-pulse-bootstrap"
                            style={{ width: 6, height: 6, backgroundColor: "#22c55e", verticalAlign: "middle" }}
                          />
                          IA Active
                        </span>
                      )}
                    </div>
                    {item.meta && (
                      <div className="small mt-1" style={{ color: "rgba(229,231,235,0.72)" }}>
                        {item.meta}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

